import Phaser from 'phaser';

/**
 * Генерация 40 декоративных текстур (по 20 на биом) программным
 * рисованием через Phaser.Graphics → generateTexture. Вызывается из
 * BootScene.preload. Текстуры используются классом DecorItem на слое
 * depth=5 (новый «4-й план» между фоновыми силуэтами и платформами).
 *
 * Каждая текстура размером 16–64 px. Origin будет установлен (0.5, 1) в
 * DecorItem, поэтому в исходниках центрируем по X и располагаем «нижний
 * край» предмета у нижнего края canvas.
 */

type DrawFn = (g: Phaser.GameObjects.Graphics, w: number, h: number) => void;

function makeDecor(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  draw: DrawFn,
) {
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  draw(g, w, h);
  g.generateTexture(key, w, h);
  g.destroy();
}

// ---------- БИОМ 1: Готические руины / археология ----------
// Палитра: тёмный сине-серый камень + бронзовые/золотистые акценты + кость.

export function generateBiome1Decor(scene: Phaser.Scene) {
  const STONE_DARK = 0x1c2434;
  const STONE_MID = 0x2e3a4a;
  const STONE_LIGHT = 0x4a5664;
  const BRONZE = 0x8a6438;
  const GOLD = 0xd9a648;
  const GOLD_HI = 0xfde084;
  const BONE = 0xd8d2c4;
  const BONE_DARK = 0x6a6458;
  const SHADOW = 0x000000;
  const WARM = 0xc8732a;

  // 1. Обломок колонны
  makeDecor(scene, 'decor-b1-01', 24, 36, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(12, 35, 22, 4);
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(6, 6, 12, 30);
    g.fillStyle(STONE_MID, 1);
    g.fillRect(7, 6, 10, 28);
    g.fillStyle(STONE_LIGHT, 0.5);
    g.fillRect(8, 6, 2, 28);
    // Капитель сверху и базы
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(3, 30, 18, 6);
    g.fillRect(4, 4, 16, 4);
    g.fillStyle(STONE_LIGHT, 0.4);
    g.fillRect(4, 4, 16, 1);
  });

  // 2. Урна / амфора
  makeDecor(scene, 'decor-b1-02', 24, 32, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(12, 31, 18, 3);
    g.fillStyle(0x3a2818, 1);
    g.fillEllipse(12, 18, 16, 22);
    g.fillStyle(0x6a4828, 1);
    g.fillEllipse(12, 16, 14, 18);
    g.fillStyle(0x8a6438, 0.7);
    g.fillEllipse(11, 14, 6, 8);
    // Горловина
    g.fillStyle(0x3a2818, 1);
    g.fillRect(8, 4, 8, 6);
    g.fillStyle(0x6a4828, 1);
    g.fillRect(9, 4, 6, 5);
  });

  // 3. Череп
  makeDecor(scene, 'decor-b1-03', 20, 18, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(10, 17, 16, 2);
    g.fillStyle(BONE_DARK, 1);
    g.fillEllipse(10, 8, 16, 14);
    g.fillStyle(BONE, 1);
    g.fillEllipse(10, 7, 14, 12);
    // Глазницы
    g.fillStyle(0x141a22, 1);
    g.fillEllipse(7, 7, 3, 4);
    g.fillEllipse(13, 7, 3, 4);
    // Нос
    g.fillTriangle(10, 9, 9, 12, 11, 12);
    // Челюсть
    g.fillStyle(BONE, 1);
    g.fillRect(7, 13, 6, 3);
    g.fillStyle(0x141a22, 1);
    g.fillRect(8, 14, 1, 2);
    g.fillRect(11, 14, 1, 2);
  });

  // 4. Треснувшая плитка
  makeDecor(scene, 'decor-b1-04', 32, 12, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillRect(0, 10, 32, 2);
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(0, 0, 32, 11);
    g.fillStyle(STONE_MID, 1);
    g.fillRect(1, 1, 30, 9);
    // Трещины
    g.fillStyle(0x10141a, 1);
    g.fillRect(8, 1, 1, 9);
    g.fillRect(8, 5, 6, 1);
    g.fillRect(20, 1, 1, 6);
  });

  // 5. Куча обломков
  makeDecor(scene, 'decor-b1-05', 32, 18, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(16, 17, 28, 3);
    g.fillStyle(STONE_DARK, 1);
    g.fillEllipse(8, 14, 12, 8);
    g.fillEllipse(20, 13, 14, 9);
    g.fillEllipse(26, 15, 10, 6);
    g.fillStyle(STONE_MID, 1);
    g.fillEllipse(7, 13, 8, 5);
    g.fillEllipse(20, 12, 10, 6);
    g.fillStyle(STONE_LIGHT, 0.5);
    g.fillRect(5, 11, 4, 1);
    g.fillRect(17, 10, 5, 1);
  });

  // 6. Подсвечник (холодный)
  makeDecor(scene, 'decor-b1-06', 16, 36, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(8, 35, 12, 3);
    // База
    g.fillStyle(BRONZE, 1);
    g.fillRect(2, 32, 12, 3);
    g.fillRect(3, 30, 10, 2);
    // Стержень
    g.fillRect(7, 8, 2, 22);
    g.fillStyle(GOLD, 0.7);
    g.fillRect(7, 8, 1, 22);
    // Чашечка
    g.fillStyle(BRONZE, 1);
    g.fillRect(4, 6, 8, 3);
    g.fillRect(5, 4, 6, 2);
    // Свеча
    g.fillStyle(BONE, 1);
    g.fillRect(7, 0, 2, 5);
    g.fillStyle(BONE_DARK, 1);
    g.fillRect(7, 0, 1, 5);
  });

  // 7. Скамеечка
  makeDecor(scene, 'decor-b1-07', 28, 14, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillRect(2, 13, 24, 1);
    g.fillStyle(0x4a3220, 1);
    g.fillRect(0, 4, 28, 4);
    g.fillStyle(0x6a4828, 1);
    g.fillRect(1, 4, 26, 2);
    // Ножки
    g.fillStyle(0x4a3220, 1);
    g.fillRect(3, 8, 3, 5);
    g.fillRect(22, 8, 3, 5);
  });

  // 8. Свиток
  makeDecor(scene, 'decor-b1-08', 20, 14, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(10, 13, 16, 2);
    g.fillStyle(0x6a5a3a, 1);
    g.fillRect(2, 4, 16, 8);
    g.fillStyle(0xa89860, 1);
    g.fillRect(3, 5, 14, 6);
    // Концы (свёрнутые)
    g.fillStyle(0x4a3a20, 1);
    g.fillEllipse(2, 8, 4, 8);
    g.fillEllipse(18, 8, 4, 8);
    g.fillStyle(0x6a5a3a, 1);
    g.fillEllipse(2, 8, 2, 6);
    g.fillEllipse(18, 8, 2, 6);
    // Линии текста
    g.fillStyle(0x2a1f10, 1);
    g.fillRect(5, 7, 8, 1);
    g.fillRect(5, 9, 6, 1);
  });

  // 9. Старая книга
  makeDecor(scene, 'decor-b1-09', 22, 16, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(11, 15, 18, 2);
    g.fillStyle(0x3a1810, 1);
    g.fillRect(2, 4, 18, 11);
    g.fillStyle(0x6a2818, 1);
    g.fillRect(3, 4, 16, 10);
    // Страницы (бок)
    g.fillStyle(0xc4ac80, 1);
    g.fillRect(3, 13, 16, 1);
    g.fillRect(3, 11, 16, 1);
    // Золотая застёжка
    g.fillStyle(GOLD, 1);
    g.fillRect(10, 8, 2, 4);
    g.fillStyle(GOLD_HI, 0.8);
    g.fillRect(10, 8, 1, 4);
  });

  // 10. Меч в земле
  makeDecor(scene, 'decor-b1-10', 16, 38, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(8, 37, 12, 2);
    // Земля у основания
    g.fillStyle(STONE_DARK, 1);
    g.fillEllipse(8, 34, 14, 4);
    // Рукоять
    g.fillStyle(0x4a3220, 1);
    g.fillRect(7, 0, 2, 6);
    g.fillStyle(BRONZE, 1);
    g.fillRect(4, 5, 8, 2);
    // Клинок
    g.fillStyle(0xa8a8b0, 1);
    g.fillRect(7, 7, 2, 26);
    g.fillStyle(0xe8e8f0, 0.7);
    g.fillRect(7, 7, 1, 26);
  });

  // 11. Шлем
  makeDecor(scene, 'decor-b1-11', 22, 18, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(11, 17, 18, 2);
    g.fillStyle(0x2a323a, 1);
    g.fillEllipse(11, 10, 18, 14);
    g.fillStyle(0x4a525a, 1);
    g.fillEllipse(11, 9, 16, 12);
    // Прорезь для глаз
    g.fillStyle(0x000000, 1);
    g.fillRect(5, 9, 12, 2);
    // Бронзовый кант
    g.fillStyle(BRONZE, 1);
    g.fillRect(3, 8, 16, 1);
    // Гребень
    g.fillStyle(0x6a2828, 1);
    g.fillRect(10, 0, 2, 5);
  });

  // 12. Знамя/флаг
  makeDecor(scene, 'decor-b1-12', 22, 38, (g) => {
    // Палка
    g.fillStyle(0x4a3220, 1);
    g.fillRect(2, 0, 1, 38);
    g.fillStyle(BRONZE, 1);
    g.fillRect(1, 0, 3, 2);
    // Полотнище
    g.fillStyle(0x3a1818, 1);
    g.fillRect(3, 4, 16, 22);
    g.fillStyle(0x6a2828, 1);
    g.fillRect(3, 4, 16, 20);
    // Зубчики снизу
    g.fillTriangle(3, 24, 6, 24, 4, 30);
    g.fillTriangle(7, 24, 11, 24, 9, 30);
    g.fillTriangle(12, 24, 16, 24, 14, 30);
    // Эмблема — крест
    g.fillStyle(GOLD, 1);
    g.fillRect(10, 10, 2, 8);
    g.fillRect(8, 12, 6, 2);
  });

  // 13. Куча монет
  makeDecor(scene, 'decor-b1-13', 24, 12, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(12, 11, 20, 2);
    // Кучка
    g.fillStyle(GOLD, 1);
    g.fillEllipse(12, 9, 18, 5);
    g.fillEllipse(8, 6, 6, 4);
    g.fillEllipse(15, 5, 5, 3);
    g.fillEllipse(11, 4, 4, 3);
    // Блики
    g.fillStyle(GOLD_HI, 1);
    g.fillEllipse(8, 6, 4, 2);
    g.fillEllipse(15, 5, 3, 2);
    g.fillEllipse(11, 4, 2, 1);
    // Тёмные тени
    g.fillStyle(0x6a4818, 1);
    g.fillEllipse(12, 10, 16, 2);
  });

  // 14. Шестерня (стимпанк)
  makeDecor(scene, 'decor-b1-14', 22, 22, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(11, 21, 18, 2);
    // Зубцы
    g.fillStyle(BRONZE, 1);
    g.fillRect(10, 1, 2, 4);
    g.fillRect(10, 17, 2, 4);
    g.fillRect(1, 10, 4, 2);
    g.fillRect(17, 10, 4, 2);
    g.fillRect(4, 4, 2, 2);
    g.fillRect(16, 4, 2, 2);
    g.fillRect(4, 16, 2, 2);
    g.fillRect(16, 16, 2, 2);
    // Тело шестерни
    g.fillStyle(BRONZE, 1);
    g.fillCircle(11, 11, 8);
    g.fillStyle(GOLD, 1);
    g.fillCircle(11, 11, 7);
    g.fillStyle(BRONZE, 1);
    g.fillCircle(11, 11, 4);
    g.fillStyle(0x000000, 1);
    g.fillCircle(11, 11, 2);
  });

  // 15. Бутылочка
  makeDecor(scene, 'decor-b1-15', 14, 22, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(7, 21, 10, 2);
    // Тело
    g.fillStyle(0x2a4a4a, 1);
    g.fillEllipse(7, 14, 12, 14);
    g.fillStyle(0x4a8a8a, 1);
    g.fillEllipse(7, 13, 10, 12);
    g.fillStyle(0x8acaca, 0.6);
    g.fillEllipse(5, 12, 3, 6);
    // Горлышко
    g.fillStyle(0x2a4a4a, 1);
    g.fillRect(5, 4, 4, 5);
    g.fillStyle(0x4a8a8a, 1);
    g.fillRect(5, 4, 3, 5);
    // Пробка
    g.fillStyle(0x4a3220, 1);
    g.fillRect(4, 1, 6, 3);
  });

  // 16. Подставка для свечи
  makeDecor(scene, 'decor-b1-16', 14, 18, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(7, 17, 10, 2);
    // База
    g.fillStyle(BRONZE, 1);
    g.fillRect(2, 14, 10, 3);
    g.fillRect(3, 12, 8, 2);
    // Стержень
    g.fillRect(6, 6, 2, 8);
    // Чаша
    g.fillRect(4, 4, 6, 3);
    // Огонёк (тёплый)
    g.fillStyle(WARM, 1);
    g.fillEllipse(7, 2, 3, 4);
    g.fillStyle(GOLD_HI, 1);
    g.fillEllipse(7, 2, 1.5, 2);
  });

  // 17. Лозы на стене
  makeDecor(scene, 'decor-b1-17', 24, 32, (g) => {
    g.fillStyle(0x2a3a1a, 1);
    // Главный стебель
    g.fillRect(11, 0, 2, 30);
    // Ветви
    g.fillRect(8, 6, 4, 1);
    g.fillRect(13, 12, 6, 1);
    g.fillRect(6, 18, 6, 1);
    g.fillRect(13, 24, 4, 1);
    // Листочки
    g.fillStyle(0x3a5a2a, 1);
    g.fillEllipse(7, 7, 4, 3);
    g.fillEllipse(18, 13, 4, 3);
    g.fillEllipse(5, 19, 4, 3);
    g.fillEllipse(16, 25, 3, 2);
    g.fillStyle(0x5a8a3a, 0.6);
    g.fillEllipse(7, 6, 2, 1);
    g.fillEllipse(18, 12, 2, 1);
  });

  // 18. Трещина в полу
  makeDecor(scene, 'decor-b1-18', 28, 8, (g) => {
    // Глубокая трещина — узкая зигзаг с тёплым свечением внутри
    g.fillStyle(0x000000, 1);
    g.fillRect(0, 3, 8, 1);
    g.fillRect(7, 4, 6, 1);
    g.fillRect(12, 3, 8, 1);
    g.fillRect(19, 4, 9, 1);
    g.fillStyle(WARM, 0.5);
    g.fillRect(2, 3, 6, 1);
    g.fillRect(13, 3, 6, 1);
    g.fillStyle(0xff9050, 0.7);
    g.fillRect(4, 3, 2, 1);
    g.fillRect(15, 3, 2, 1);
  });

  // 19. Рунный камень
  makeDecor(scene, 'decor-b1-19', 18, 28, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(9, 27, 14, 2);
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(2, 6, 14, 22);
    g.fillStyle(STONE_MID, 1);
    g.fillRect(3, 6, 12, 20);
    // Закруглённый верх
    g.fillStyle(STONE_DARK, 1);
    g.fillEllipse(9, 6, 14, 6);
    g.fillStyle(STONE_MID, 1);
    g.fillEllipse(9, 6, 12, 5);
    // Руна — светящийся символ
    g.fillStyle(WARM, 0.7);
    g.fillRect(7, 12, 4, 2);
    g.fillRect(8, 10, 2, 6);
    g.fillRect(6, 18, 6, 1);
    g.fillStyle(0xff9050, 0.9);
    g.fillRect(8, 11, 2, 4);
  });

  // 20. Маленький памятник
  makeDecor(scene, 'decor-b1-20', 22, 36, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(11, 35, 18, 3);
    // Базы (3 ступени)
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(0, 30, 22, 5);
    g.fillRect(2, 26, 18, 4);
    g.fillStyle(STONE_MID, 1);
    g.fillRect(0, 30, 22, 1);
    g.fillRect(2, 26, 18, 1);
    // Стела
    g.fillStyle(STONE_DARK, 1);
    g.fillRect(7, 4, 8, 22);
    g.fillStyle(STONE_MID, 1);
    g.fillRect(8, 4, 6, 22);
    // Вершина — арочка
    g.fillStyle(STONE_DARK, 1);
    g.fillEllipse(11, 4, 8, 4);
    // Бронзовая табличка
    g.fillStyle(BRONZE, 1);
    g.fillRect(8, 12, 6, 8);
    g.fillStyle(GOLD, 0.5);
    g.fillRect(9, 13, 1, 6);
  });
}

// ---------- БИОМ 2: Абисс / пещеры ----------
// Палитра: чёрный камень + малиновое свечение + холодный циан/фиолет.

export function generateBiome2Decor(scene: Phaser.Scene) {
  const ROCK_DARK = 0x0a0510;
  const ROCK_MID = 0x1a0e1a;
  const ROCK_LIGHT = 0x2a1828;
  const MAGENTA_DEEP = 0x6a1a40;
  const MAGENTA_MID = 0xc83c80;
  const MAGENTA_GLOW = 0xff6aa8;
  const MAGENTA_HI = 0xffd0e8;
  const COLD = 0x4a8a9a;
  const COLD_HI = 0x8acccd;
  const SHADOW = 0x000000;

  // 1. Скопление магентовых кристаллов
  makeDecor(scene, 'decor-b2-01', 26, 32, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(13, 31, 22, 3);
    // Малиновый ореол
    g.fillStyle(MAGENTA_DEEP, 0.4);
    g.fillEllipse(13, 18, 24, 24);
    // Кристалл 1 (большой)
    g.fillStyle(ROCK_DARK, 1);
    g.fillTriangle(8, 30, 12, 6, 16, 30);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillTriangle(9, 28, 12, 8, 15, 28);
    g.fillStyle(MAGENTA_GLOW, 0.9);
    g.fillTriangle(11, 24, 12, 10, 13, 24);
    g.fillStyle(MAGENTA_HI, 1);
    g.fillTriangle(11, 20, 12, 12, 12.5, 20);
    // Кристалл 2 (поменьше)
    g.fillStyle(MAGENTA_MID, 1);
    g.fillTriangle(17, 30, 20, 16, 23, 30);
    g.fillStyle(MAGENTA_GLOW, 0.85);
    g.fillTriangle(18, 28, 20, 18, 22, 28);
    // Кристалл 3
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillTriangle(2, 30, 5, 22, 8, 30);
    g.fillStyle(MAGENTA_MID, 0.9);
    g.fillTriangle(3, 28, 5, 24, 7, 28);
  });

  // 2. Сталактит (свисает сверху)
  makeDecor(scene, 'decor-b2-02', 14, 30, (g) => {
    g.fillStyle(ROCK_DARK, 1);
    g.fillTriangle(7, 30, 0, 0, 14, 0);
    g.fillStyle(ROCK_MID, 1);
    g.fillTriangle(7, 27, 2, 0, 12, 0);
    g.fillStyle(ROCK_LIGHT, 0.6);
    g.fillTriangle(7, 23, 4, 0, 6, 0);
    // Капля магенты на конце
    g.fillStyle(MAGENTA_DEEP, 0.6);
    g.fillEllipse(7, 28, 6, 4);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillEllipse(7, 28, 3, 2);
  });

  // 3. Тёмная свисающая лоза
  makeDecor(scene, 'decor-b2-03', 14, 32, (g) => {
    g.fillStyle(0x140a14, 1);
    g.fillRect(6, 0, 2, 28);
    // Изгибы
    g.fillRect(4, 8, 2, 1);
    g.fillRect(8, 14, 2, 1);
    g.fillRect(3, 22, 3, 1);
    // «Бутоны»
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillCircle(5, 12, 2);
    g.fillCircle(10, 18, 2);
    g.fillCircle(4, 26, 3);
    g.fillStyle(MAGENTA_MID, 0.8);
    g.fillCircle(4, 26, 2);
    g.fillStyle(MAGENTA_GLOW, 0.9);
    g.fillCircle(4, 26, 1);
  });

  // 4. Куча тёмных камней
  makeDecor(scene, 'decor-b2-04', 30, 16, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(15, 15, 26, 2);
    g.fillStyle(ROCK_DARK, 1);
    g.fillEllipse(7, 12, 12, 8);
    g.fillEllipse(18, 11, 14, 9);
    g.fillEllipse(25, 13, 10, 6);
    g.fillStyle(ROCK_MID, 1);
    g.fillEllipse(7, 11, 8, 5);
    g.fillEllipse(18, 10, 10, 6);
    // Малиновые трещины
    g.fillStyle(MAGENTA_MID, 0.7);
    g.fillRect(5, 10, 4, 1);
    g.fillRect(17, 9, 5, 1);
  });

  // 5. Светящаяся лужа
  makeDecor(scene, 'decor-b2-05', 32, 8, (g) => {
    g.fillStyle(MAGENTA_DEEP, 0.4);
    g.fillEllipse(16, 4, 32, 8);
    g.fillStyle(MAGENTA_MID, 0.7);
    g.fillEllipse(16, 4, 26, 6);
    g.fillStyle(MAGENTA_GLOW, 0.9);
    g.fillEllipse(16, 4, 16, 4);
    g.fillStyle(MAGENTA_HI, 0.85);
    g.fillEllipse(14, 3, 6, 2);
  });

  // 6. Минеральная жила (наклонная)
  makeDecor(scene, 'decor-b2-06', 32, 16, (g) => {
    g.fillStyle(ROCK_DARK, 0.4);
    g.fillRect(0, 4, 32, 8);
    // Светящаяся жила
    g.fillStyle(MAGENTA_DEEP, 0.7);
    g.fillRect(0, 7, 32, 4);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillRect(2, 8, 28, 2);
    g.fillStyle(MAGENTA_GLOW, 0.9);
    g.fillRect(4, 8, 24, 1);
    g.fillStyle(MAGENTA_HI, 0.7);
    g.fillRect(8, 8, 4, 1);
    g.fillRect(20, 8, 4, 1);
  });

  // 7. Сломанный кокон
  makeDecor(scene, 'decor-b2-07', 24, 24, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(12, 23, 20, 2);
    g.fillStyle(0x2a1830, 1);
    g.fillEllipse(12, 14, 22, 18);
    g.fillStyle(0x4a2848, 1);
    g.fillEllipse(12, 13, 18, 14);
    // Трещина
    g.fillStyle(MAGENTA_DEEP, 0.8);
    g.fillTriangle(8, 4, 16, 4, 12, 12);
    g.fillStyle(MAGENTA_MID, 0.9);
    g.fillTriangle(9, 5, 15, 5, 12, 11);
    g.fillStyle(MAGENTA_GLOW, 0.8);
    g.fillRect(11, 5, 2, 6);
  });

  // 8. Цветок абисса
  makeDecor(scene, 'decor-b2-08', 22, 28, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(11, 27, 14, 2);
    // Стебель
    g.fillStyle(0x140a14, 1);
    g.fillRect(10, 14, 2, 14);
    // Лепестки (4 крест-накрест)
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillEllipse(11, 8, 8, 12);
    g.fillEllipse(11, 8, 14, 6);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillEllipse(11, 8, 6, 10);
    g.fillEllipse(11, 8, 11, 4);
    // Сердцевина
    g.fillStyle(MAGENTA_GLOW, 1);
    g.fillCircle(11, 8, 2.5);
    g.fillStyle(MAGENTA_HI, 0.9);
    g.fillCircle(11, 8, 1);
  });

  // 9. Сталагмит (растёт снизу)
  makeDecor(scene, 'decor-b2-09', 16, 28, (g) => {
    g.fillStyle(SHADOW, 0.4);
    g.fillEllipse(8, 27, 14, 3);
    g.fillStyle(ROCK_DARK, 1);
    g.fillTriangle(8, 0, 0, 28, 16, 28);
    g.fillStyle(ROCK_MID, 1);
    g.fillTriangle(8, 4, 2, 28, 14, 28);
    g.fillStyle(ROCK_LIGHT, 0.6);
    g.fillTriangle(8, 8, 5, 28, 11, 28);
    // Малиновый блик у вершины
    g.fillStyle(MAGENTA_MID, 0.6);
    g.fillEllipse(8, 2, 4, 3);
  });

  // 10. Осколок пустоты
  makeDecor(scene, 'decor-b2-10', 18, 24, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(9, 23, 12, 2);
    g.fillStyle(MAGENTA_DEEP, 0.5);
    g.fillEllipse(9, 12, 18, 16);
    g.fillStyle(ROCK_DARK, 1);
    g.fillTriangle(9, 0, 0, 22, 18, 22);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillTriangle(9, 3, 3, 21, 15, 21);
    g.fillStyle(MAGENTA_GLOW, 0.9);
    g.fillTriangle(9, 6, 5, 19, 13, 19);
    g.fillStyle(MAGENTA_HI, 1);
    g.fillTriangle(9, 8, 7, 16, 11, 16);
  });

  // 11. Светящийся щупалец
  makeDecor(scene, 'decor-b2-11', 12, 32, (g) => {
    // S-изгиб
    g.fillStyle(0x140a14, 1);
    g.fillRect(5, 0, 2, 12);
    g.fillRect(3, 12, 2, 8);
    g.fillRect(5, 20, 2, 12);
    // Маленькие свечения по длине
    g.fillStyle(MAGENTA_GLOW, 0.85);
    g.fillCircle(6, 6, 1.5);
    g.fillCircle(4, 16, 1.5);
    g.fillCircle(6, 26, 1.5);
    g.fillStyle(MAGENTA_HI, 1);
    g.fillCircle(6, 6, 0.7);
    g.fillCircle(4, 16, 0.7);
    g.fillCircle(6, 26, 0.7);
  });

  // 12. Облачко спор
  makeDecor(scene, 'decor-b2-12', 26, 14, (g) => {
    g.fillStyle(MAGENTA_DEEP, 0.4);
    g.fillEllipse(13, 7, 24, 12);
    g.fillStyle(MAGENTA_MID, 0.5);
    g.fillEllipse(13, 6, 20, 10);
    g.fillStyle(MAGENTA_GLOW, 0.6);
    g.fillEllipse(13, 6, 14, 6);
    // Светящиеся точки
    g.fillStyle(MAGENTA_HI, 1);
    g.fillCircle(8, 5, 1);
    g.fillCircle(13, 4, 1);
    g.fillCircle(18, 6, 1);
    g.fillCircle(11, 8, 0.8);
    g.fillCircle(16, 9, 0.8);
  });

  // 13. Пещерный мох
  makeDecor(scene, 'decor-b2-13', 28, 8, (g) => {
    g.fillStyle(0x1a3030, 1);
    g.fillRect(0, 4, 28, 4);
    g.fillStyle(0x2a4a4a, 1);
    g.fillRect(0, 4, 28, 2);
    // «Травинки»
    g.fillStyle(COLD, 1);
    for (let i = 1; i < 28; i += 3) {
      g.fillRect(i, 2, 1, 4);
    }
    g.fillStyle(COLD_HI, 0.7);
    for (let i = 2; i < 28; i += 5) {
      g.fillRect(i, 2, 1, 2);
    }
  });

  // 14. Расщелина с малиновым свечением
  makeDecor(scene, 'decor-b2-14', 24, 28, (g) => {
    g.fillStyle(ROCK_DARK, 1);
    g.fillRect(2, 0, 20, 28);
    g.fillStyle(ROCK_MID, 1);
    g.fillRect(3, 1, 18, 26);
    // Расщелина зигзаг
    g.fillStyle(SHADOW, 1);
    g.fillTriangle(11, 4, 14, 12, 8, 16);
    g.fillTriangle(8, 16, 14, 22, 12, 26);
    // Свечение
    g.fillStyle(MAGENTA_DEEP, 0.7);
    g.fillTriangle(12, 6, 13, 12, 9, 15);
    g.fillStyle(MAGENTA_MID, 0.9);
    g.fillTriangle(11, 8, 12, 12, 10, 14);
    g.fillStyle(MAGENTA_GLOW, 0.85);
    g.fillRect(11, 9, 1, 5);
    g.fillRect(10, 18, 1, 6);
  });

  // 15. Кристаллический столбик
  makeDecor(scene, 'decor-b2-15', 14, 36, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(7, 35, 12, 2);
    // База
    g.fillStyle(ROCK_DARK, 1);
    g.fillRect(2, 30, 10, 5);
    // Стержень — кристалл
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillRect(5, 6, 4, 26);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillRect(6, 6, 2, 26);
    g.fillStyle(MAGENTA_GLOW, 0.85);
    g.fillRect(6, 8, 1, 22);
    // Острая вершина
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillTriangle(2, 6, 7, 0, 12, 6);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillTriangle(4, 6, 7, 1, 10, 6);
    g.fillStyle(MAGENTA_HI, 0.9);
    g.fillTriangle(6, 6, 7, 2, 8, 6);
  });

  // 16. Декоративный пульсирующий шар
  makeDecor(scene, 'decor-b2-16', 16, 22, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(8, 21, 12, 2);
    // Подставка-кристаллический штатив
    g.fillStyle(ROCK_DARK, 1);
    g.fillRect(4, 14, 8, 6);
    g.fillStyle(ROCK_MID, 1);
    g.fillRect(5, 14, 6, 5);
    // Свечение вокруг
    g.fillStyle(MAGENTA_DEEP, 0.5);
    g.fillCircle(8, 8, 8);
    g.fillStyle(MAGENTA_MID, 0.8);
    g.fillCircle(8, 8, 6);
    g.fillStyle(MAGENTA_GLOW, 1);
    g.fillCircle(8, 8, 4);
    g.fillStyle(MAGENTA_HI, 0.9);
    g.fillCircle(7, 7, 1.5);
  });

  // 17. Капельная лужа
  makeDecor(scene, 'decor-b2-17', 18, 6, (g) => {
    g.fillStyle(MAGENTA_DEEP, 0.5);
    g.fillEllipse(9, 3, 16, 5);
    g.fillStyle(MAGENTA_MID, 0.8);
    g.fillEllipse(9, 3, 12, 4);
    g.fillStyle(MAGENTA_HI, 0.7);
    g.fillEllipse(7, 2, 4, 1);
  });

  // 18. Фрагмент кости
  makeDecor(scene, 'decor-b2-18', 22, 8, (g) => {
    g.fillStyle(SHADOW, 0.3);
    g.fillEllipse(11, 7, 18, 2);
    g.fillStyle(0x6a6458, 1);
    g.fillRect(2, 2, 18, 4);
    g.fillStyle(0xa89c88, 1);
    g.fillRect(3, 2, 16, 3);
    // Шарики на концах
    g.fillStyle(0x6a6458, 1);
    g.fillCircle(2, 4, 3);
    g.fillCircle(20, 4, 3);
    g.fillStyle(0xa89c88, 1);
    g.fillCircle(2, 4, 2);
    g.fillCircle(20, 4, 2);
  });

  // 19. Паутина
  makeDecor(scene, 'decor-b2-19', 28, 24, (g) => {
    g.lineStyle(1, 0x8a8888, 0.7);
    // Радиальные линии
    g.beginPath();
    g.moveTo(14, 0);
    g.lineTo(14, 24);
    g.moveTo(0, 12);
    g.lineTo(28, 12);
    g.moveTo(2, 2);
    g.lineTo(26, 22);
    g.moveTo(26, 2);
    g.lineTo(2, 22);
    g.strokePath();
    // Концентрические
    g.lineStyle(1, 0xa8a8a8, 0.5);
    g.strokeCircle(14, 12, 8);
    g.strokeCircle(14, 12, 5);
    g.strokeCircle(14, 12, 2);
    // Маленький паук-силуэт в центре
    g.fillStyle(0x000000, 0.9);
    g.fillCircle(14, 12, 2);
  });

  // 20. Грибной нарост
  makeDecor(scene, 'decor-b2-20', 22, 22, (g) => {
    g.fillStyle(SHADOW, 0.35);
    g.fillEllipse(11, 21, 18, 2);
    // Ножка
    g.fillStyle(0x4a3a4a, 1);
    g.fillRect(9, 12, 4, 9);
    g.fillStyle(0x6a4a6a, 1);
    g.fillRect(9, 12, 2, 9);
    // Шляпка
    g.fillStyle(MAGENTA_DEEP, 1);
    g.fillEllipse(11, 10, 18, 12);
    g.fillStyle(MAGENTA_MID, 1);
    g.fillEllipse(11, 9, 14, 8);
    g.fillStyle(MAGENTA_GLOW, 0.85);
    g.fillEllipse(11, 8, 8, 4);
    // Точечки на шляпке
    g.fillStyle(MAGENTA_HI, 0.9);
    g.fillCircle(7, 9, 1);
    g.fillCircle(14, 8, 1);
    g.fillCircle(11, 11, 0.8);
  });
}
